import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { t } from '../common/i18n';
import { useAppDispatch, useAppSelector, useDocumentTitle } from '../common/hooks';
import routes from '../common/constants/routes';
import {
  Button,
  Card,
  CardUserList,
  Content,
  FileField,
  FormMessage,
  Header,
  Icon,
  Modal,
  RadioField,
  ReadReceiptBadge,
  SingleSelectField,
  SubscriptionModal,
  Text,
  TipMessage,
} from '../common/components';
import { useModal } from '../common/components/Modal/useModal';
import BuildingLogsTimeline from '../Registry/components/buildingLog/BuildingLogsTimeline';
import TimelineEmptyState from '../Registry/components/buildingLog/TimelineEmptyState';

import { getUnit, updateUnitVotingStrategy } from './services/UnitService';
import {
  IRepresentationRecord,
  downloadRepresentationFile,
  endDesignatedVoter,
  getDesignatedVoter,
  setDesignatedVoter,
} from './services/RepresentationService';
import { RepresentativeForm } from './components/RepresentativeForm';
import { TSFixMe } from '../frontend-libs/evlapp-types';
import { getProfileName } from '../common/utils/Profile/profile';
import { useFormik } from 'formik';
import Yup from '../common/utils/FormValidation';
import { selectSecurity } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import { ModalDelete } from '../FileStorage/components/ModalDelete';
import { ModalRename } from '../FileStorage/components/ModalRename';
import { UNIT_FILE_CATEGORIES } from '../FileStorage/config/fileCategories';
import { downloadStorageFile, getUnitFiles, IFile, uploadFiles } from '../FileStorage/services/FilesService';

// votingStrategy hodnota z API → i18n klíč popisku (Form.Fields.VotingStrategy.*)
const VOTING_STRATEGY_LABEL_KEYS: Record<string, string> = {
  first_vote: 'default',
  with_quorum: 'separateVoting',
  without_vote: 'excludedFromVoting',
  designated_voter: 'designatedVoter',
};

type UnitDetailTab = 'overview' | 'files' | 'history';

export const UnitDetail: React.FC = () => {
  const namespace = 'Units.UnitDetail';
  useDocumentTitle(t('title', namespace));

  const dispatch = useAppDispatch();
  const security = useAppSelector(selectSecurity).ui;
  const isAdmin = security.isAdmin;
  const hasSubscription = security.tokens?.filter((item) => item.type === 'building-subscription').length || 0;
  const shouldShowModal = !hasSubscription;

  const { id = null } = useParams();
  const [unit, setUnit] = useState<TSFixMe>({});
  const hasOwners = unit.owners || false;
  const unitNumber = unit?.label || '';
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isEditingVoting, setIsEditingVoting] = useState(false);
  const [activeTab, setActiveTab] = useState<UnitDetailTab>('overview');
  const [designatedVoter, setDesignatedVoterState] = useState<IRepresentationRecord | null>(null);

  const fetchDesignatedVoter = async (unitId: string) => {
    try {
      const data = await getDesignatedVoter(unitId);
      setDesignatedVoterState(data.active);
    } catch {
      setDesignatedVoterState(null);
    }
  };

  // Stabilní identita — inline objekt by v useBuildingLogs spouštěl refetch v každém renderu
  const logFilters = useMemo(() => ({ units: id !== null ? [id] : [] }), [id]);

  const [unitFiles, setUnitFiles] = useState<IFile[]>([]);
  const [activeFile, setActiveFile] = useState<TSFixMe>();
  const [uploadError, setUploadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isShownUpload, toggleUpload] = useModal();
  const [isShownRename, toggleRename] = useModal();
  const [isShownDelete, toggleDelete] = useModal();

  const fetchUnitFiles = async () => {
    if (id === null) return;
    try {
      const files = await getUnitFiles(id);
      setUnitFiles(files.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    } catch {
      setUnitFiles([]);
    }
  };

  const formik = useFormik<{ votingStrategy: string }>({
    initialValues: { votingStrategy: 'first_vote' },
    // validate: () => setFormError(false),
    validationSchema: Yup.object({
      votingStrategy: Yup.string().required(),
    }),
    onSubmit: (values) => {
      if (id === null) {
        formik.setFieldError('votingStrategy', t('Form.error', namespace));
        formik.setSubmitting(false);
        return;
      }

      updateUnitVotingStrategy(id, values)
        .then(() => {
          setUnit((prev: TSFixMe) => ({ ...prev, votingStrategy: values.votingStrategy }));
          // Odchod z režimu společného zástupce ukončuje evidenci i na BE
          setDesignatedVoterState(null);
          setIsEditingVoting(false);
          dispatch(setFlashMessage({ message: t('Form.success', namespace), type: 'success' }));
        })
        .catch(() => formik.setFieldError('votingStrategy', t('Form.error', namespace)))
        .finally(() => formik.setSubmitting(false));
    },
  });

  useEffect(() => {
    async function fetchUnit() {
      try {
        const data = await getUnit(id);
        setUnit(data);

        formik.setFieldValue('votingStrategy', data.votingStrategy);
        if (isAdmin && data.votingStrategy === 'designated_voter' && id !== null) {
          fetchDesignatedVoter(id);
        }
        setIsLoading(false);
      } catch {
        setIsEmpty(true);
        setIsLoading(false);
      }
    }
    fetchUnit();
    if (isAdmin) fetchUnitFiles();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdmin]);

  const uploadFormik = useFormik<TSFixMe>({
    initialValues: { attachment: null, category: 'other' },
    validate: () => setUploadError(false),
    validationSchema: Yup.object({
      attachment: Yup.string().nullable().required(),
    }),
    onSubmit: (values) => {
      if (id === null) return;

      const formData = new FormData();
      for (let i = 0; i < values.attachment.length; i++) {
        formData.append(`files[${i}]`, values.attachment[i]);
      }
      formData.append('unitId', id);
      formData.append('category', values.category);

      uploadFiles(formData)
        .then(() => {
          fetchUnitFiles().then(() => {
            uploadFormik.setSubmitting(false);
            uploadFormik.resetForm();
            if (fileInputRef.current) fileInputRef.current.value = '';
            dispatch(setFlashMessage({ message: t('Files.ModalUpload.success', namespace), type: 'success' }));
            toggleUpload();
          });
        })
        .catch(() => {
          setUploadError(true);
          uploadFormik.setSubmitting(false);
        });
    },
  });

  const categoryOptions = useMemo(
    () => UNIT_FILE_CATEGORIES.map((category) => ({ value: category, label: t(`Category.${category}`, 'Files') })),
    []
  );
  const categoryValue = categoryOptions.find((option) => option.value === uploadFormik.values.category) ?? null;

  return (
    <>
      <Header
        title={`${t('Header.title', namespace)} ${unitNumber ? `č.${unitNumber}` : ''}`}
        customBacklink={routes.units}
      />
      <Content isLoading={isLoading} isEmpty={isEmpty}>
        {isAdmin && (
          <div className="UnitTabs">
            <button
              type="button"
              className={`UnitTabs__item ${activeTab === 'overview' ? 'UnitTabs__item--active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <Icon name="home" color="Inherit" />
              {t('Tabs.overview', namespace)}
            </button>
            <button
              type="button"
              className={`UnitTabs__item ${activeTab === 'files' ? 'UnitTabs__item--active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              <Icon name="document" color="Inherit" />
              {t('Tabs.files', namespace)}
              {unitFiles.length > 0 && <span className="UnitTabs__badge">{unitFiles.length}</span>}
            </button>
            <button
              type="button"
              className={`UnitTabs__item ${activeTab === 'history' ? 'UnitTabs__item--active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <Icon name="flag" color="Inherit" />
              {t('Tabs.history', namespace)}
            </button>
          </div>
        )}

        {(activeTab === 'overview' || !isAdmin) && (
          <>
            <div className="UnitStats">
              {unit?.useType && (
                <Card className="UnitStat">
                  <Icon name="home" color="AccentOrange" className="UnitStat__icon" />
                  <Text size="body-small" weight="500" color="Neutral600">
                    {t('Form.Fields.UseType.label', namespace)}
                  </Text>
                  <Text size="h3" color="Neutral800" className="UnitStat__value UnitStat__value--capitalize">
                    {t(`Enums.UnitUseType.${unit?.useType}`).toLowerCase()}
                  </Text>
                </Card>
              )}

              {unit.share && unit.share.numerator && unit.share.denominator && (
                <Card className="UnitStat">
                  <Icon name="components" color="AccentOrange" className="UnitStat__icon" />
                  <Text size="body-small" weight="500" color="Neutral600">
                    {t('Form.Fields.share', namespace)}
                  </Text>
                  <Text size="h3" color="Neutral800" className="UnitStat__value">
                    {unit.share.numerator}/{unit.share.denominator}
                  </Text>
                  <Text size="body-small" color="Neutral600">
                    {t('Stats.shareOfBuilding', namespace, {
                      percent: ((unit.share.numerator / unit.share.denominator) * 100).toLocaleString('cs-CZ', {
                        maximumFractionDigits: 1,
                      }),
                    })}
                  </Text>
                </Card>
              )}

              <Card className="UnitStat">
                <Icon name="checkmark-circle" color="AccentOrange" className="UnitStat__icon" />
                <Text size="body-small" weight="500" color="Neutral600">
                  {t('Stats.voting', namespace)}
                </Text>
                <Text size="h3" color="Neutral800" className="UnitStat__value">
                  {t(
                    `Form.Fields.VotingStrategy.short_${VOTING_STRATEGY_LABEL_KEYS[unit.votingStrategy] ?? 'default'}`,
                    namespace
                  )}
                </Text>
                {unit.votingStrategy === 'designated_voter' && designatedVoter && (
                  <Text size="body-small" color="Neutral600">
                    {designatedVoter.representativeName}
                    {designatedVoter.validTo &&
                      ` (${t('DesignatedVoter.validTo', namespace, { date: designatedVoter.validTo })})`}
                  </Text>
                )}
                {isAdmin && (
                  <div>
                    <Button
                      variant="text"
                      size="small"
                      title={t('Form.Fields.VotingStrategy.change', namespace)}
                      onClick={() => setIsEditingVoting(true)}
                    />
                  </div>
                )}
              </Card>
            </div>

            {hasOwners && (
              <Card className="Card--content UnitOwnersCard">
                <div className="UnitOwnersCard__header">
                  <Icon name="users" color="AccentOrange" />
                  <Text size="h3" color="Neutral800">
                    {t('Form.Fields.Owners.label', namespace)}
                  </Text>
                  {unit.owners.length > 1 && <span className="UnitTabs__badge">{unit.owners.length}</span>}
                </div>
                <div className="Flex FlexWrap FlexGap--1">
                  {unit?.owners?.map((owner: TSFixMe) => {
                    const { profile } = owner;

                    return (
                      <div key={profile.id} className="UserList__cell UserList__cell--small mr-0">
                        <CardUserList
                          text={''}
                          to={`${routes.units}profile/${profile.id}`}
                          title={getProfileName(profile)}
                          variant="bordered"
                        />
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            <Modal
              isShown={isEditingVoting}
              onHide={() => {
                formik.setFieldValue('votingStrategy', unit.votingStrategy);
                setIsEditingVoting(false);
              }}
            >
              <Text size="h2" align="center" className="mb-3">
                {t('Form.Fields.VotingStrategy.label', namespace)}
              </Text>

              <form onSubmit={formik.handleSubmit}>
                <RadioField
                  name="votingStrategy"
                  value="first_vote"
                  className="mb-1"
                  label={t('Form.Fields.VotingStrategy.default', namespace)}
                  onChange={(e) => formik.setFieldValue('votingStrategy', e.target.value)}
                  checked={formik.values.votingStrategy === 'first_vote'}
                  disabled={!isAdmin}
                  description={t('Form.Fields.VotingStrategy.default_description', namespace)}
                />

                <RadioField
                  name="votingStrategy"
                  value="with_quorum"
                  className="mb-1"
                  label={t('Form.Fields.VotingStrategy.separateVoting', namespace)}
                  onChange={(e) => formik.setFieldValue('votingStrategy', e.target.value)}
                  checked={formik.values.votingStrategy === 'with_quorum'}
                  disabled={!isAdmin}
                  description={t('Form.Fields.VotingStrategy.separateVoting_description', namespace)}
                />

                <RadioField
                  name="votingStrategy"
                  value="without_vote"
                  className="mb-1"
                  label={t('Form.Fields.VotingStrategy.excludedFromVoting', namespace)}
                  onChange={(e) => formik.setFieldValue('votingStrategy', e.target.value)}
                  checked={formik.values.votingStrategy === 'without_vote'}
                  disabled={!isAdmin}
                  description={t('Form.Fields.VotingStrategy.excludedFromVoting_description', namespace)}
                />

                <RadioField
                  name="votingStrategy"
                  value="designated_voter"
                  className="mb-2"
                  label={t('Form.Fields.VotingStrategy.designatedVoter', namespace)}
                  onChange={(e) => formik.setFieldValue('votingStrategy', e.target.value)}
                  checked={formik.values.votingStrategy === 'designated_voter'}
                  disabled={!isAdmin}
                  description={t('Form.Fields.VotingStrategy.designatedVoter_description', namespace)}
                />

                {formik.values.votingStrategy !== 'designated_voter' && (
                  <div className="Flex FlexAlign--center FlexJustify--center mb-2">
                    <Button
                      onClick={formik.handleSubmit}
                      loading={formik.isSubmitting}
                      disabledDeps={['demo', 'expired']}
                      title={t('Form.submit', namespace)}
                      wide={true}
                    />
                  </div>
                )}
              </form>

              {formik.values.votingStrategy === 'designated_voter' && id !== null && (
                <>
                  <TipMessage title={t('DesignatedVoter.consentTipTitle', namespace)} className="mb-2">
                    {t('DesignatedVoter.consentTip', namespace)}
                  </TipMessage>

                  {designatedVoter && (
                    <div className="mb-2">
                      <Text as="div" size="body-medium" weight="600" color="Neutral800">
                        {t('DesignatedVoter.current', namespace, { name: designatedVoter.representativeName })}
                        {designatedVoter.validTo &&
                          ` (${t('DesignatedVoter.validTo', namespace, { date: designatedVoter.validTo })})`}
                      </Text>
                      {designatedVoter.files.map((file) => (
                        <Button
                          key={file.id}
                          variant="text"
                          size="small"
                          title={file.name}
                          onClick={() => downloadRepresentationFile(file)}
                        />
                      ))}
                      <div>
                        <Button
                          variant="text"
                          size="small"
                          title={t('DesignatedVoter.end', namespace)}
                          onClick={() => {
                            endDesignatedVoter(id).then(() => {
                              setDesignatedVoterState(null);
                              getUnit(id).then((data) => {
                                setUnit(data);
                                formik.setFieldValue('votingStrategy', data.votingStrategy);
                              });
                              setIsEditingVoting(false);
                              dispatch(
                                setFlashMessage({ message: t('DesignatedVoter.ended', namespace), type: 'success' })
                              );
                            });
                          }}
                        />
                      </div>
                      <div className="Card__divider mt-2 mb-2"></div>
                      <Text as="div" size="body-small" color="Neutral600" className="mb-1">
                        {t('DesignatedVoter.replaceHint', namespace)}
                      </Text>
                    </div>
                  )}

                  <RepresentativeForm
                    submitLabel={t('DesignatedVoter.submit', namespace)}
                    preferredProfileIds={(unit.owners ?? []).map((owner: TSFixMe) => owner.profile?.id ?? owner.id)}
                    onSubmit={async (values) => {
                      const record = await setDesignatedVoter(id, values);
                      setDesignatedVoterState(record);
                      setUnit((prev: TSFixMe) => ({ ...prev, votingStrategy: 'designated_voter' }));
                      setIsEditingVoting(false);
                      dispatch(setFlashMessage({ message: t('DesignatedVoter.success', namespace), type: 'success' }));
                    }}
                  />
                </>
              )}
            </Modal>
          </>
        )}

        {isAdmin && activeTab === 'files' && (
          <>
            <div className="Flex FlexAlign--center FlexJustify--space-between mb-2">
              <Text size="body-small" color="Neutral600">
                {t('Files.visibility', namespace)}
              </Text>
              <Button size="small" title={t('Files.upload', namespace)} onClick={toggleUpload} />
            </div>

            {unitFiles.length === 0 ? (
              <Card className="Card--content">
                <Text align="center" color="Neutral600">
                  {t('Files.empty', namespace)}
                </Text>
              </Card>
            ) : (
              <>
                <Card className="CardAttachments">
                  {unitFiles.map((item, index) => (
                    <Fragment key={item.id}>
                      <div className="Card__link">
                        <div className="mr-auto">
                          <Text size="body-medium" color="Inherit">
                            {item.filename}
                          </Text>
                          {!!item.category && item.category !== 'other' && (
                            <Text as="div" size="body-small" color="Neutral600">
                              {t(`Category.${item.category}`, 'Files')}
                            </Text>
                          )}
                        </div>

                        {/* Bez preloaded — badge si stáhne detail (publikum = vlastníci jednotky) */}
                        <ReadReceiptBadge subjectType="building-file" subjectId={item.id} className="mr-2" />

                        <Icon
                          name="download"
                          color="Inherit"
                          className="icon--clickable"
                          onClick={() => downloadStorageFile(item)}
                        />

                        <div className="CardActions">
                          <Icon
                            name="edit"
                            onClick={() => {
                              setActiveFile(item);
                              toggleRename();
                            }}
                          />
                          <Icon
                            name="trash"
                            onClick={() => {
                              setActiveFile(item);
                              toggleDelete();
                            }}
                          />
                        </div>
                      </div>
                      {index !== unitFiles.length - 1 && <div className="Card__divider"></div>}
                    </Fragment>
                  ))}
                </Card>
              </>
            )}

            <Modal isShown={isShownUpload} onHide={toggleUpload}>
              <Text size="h2" align="center" className="mb-2">
                {t('Files.ModalUpload.title', namespace)}
              </Text>
              <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
                {t('Files.ModalUpload.subtitle', namespace)}
              </Text>
              {uploadError && <FormMessage message={t('Files.ModalUpload.formError', namespace)} variant="dark" />}

              <SingleSelectField
                label={t('Files.ModalUpload.Category.label', namespace)}
                name="category"
                value={categoryValue}
                onChange={(option) =>
                  uploadFormik.setFieldValue('category', (option as { value: string } | null)?.value ?? 'other')
                }
                options={categoryOptions}
                className="mb-2"
              />

              <FileField
                name="attachment"
                label={t('Files.ModalUpload.Attachment.label', namespace)}
                filesLabel="Files.ModalUpload.Attachment.filesLabel"
                multiple={true}
                reference={fileInputRef}
                formikValue={uploadFormik.values.attachment}
                className="mt-2 mb-4"
                error={{
                  message: uploadFormik.errors.attachment as string,
                  touched: uploadFormik.touched.attachment as boolean,
                }}
                onChange={(event) => {
                  if (event && event.currentTarget && event.currentTarget.files) {
                    uploadFormik.setFieldValue('attachment', event.currentTarget.files);
                  }
                }}
              />

              <div className="Flex FlexAlign--center FlexJustify--center mb-2">
                <Button
                  onClick={uploadFormik.handleSubmit}
                  title={t('Files.ModalUpload.submit', namespace)}
                  loading={uploadFormik.isSubmitting}
                  disabledDeps={['demo', 'expired']}
                  wide={true}
                />
              </div>
            </Modal>
            <ModalDelete
              id={activeFile?.id || ''}
              isShown={isShownDelete}
              onHide={toggleDelete}
              handleFetchFiles={fetchUnitFiles}
            />
            <ModalRename
              file={activeFile}
              isShown={isShownRename}
              onHide={toggleRename}
              handleFetchFiles={fetchUnitFiles}
            />
          </>
        )}

        {isAdmin && activeTab === 'history' && id !== null && (
          <div className="Registry">
            <div className="timeline-container">
              <div className={shouldShowModal ? 'blur-content timeline-locked' : ''}>
                <BuildingLogsTimeline
                  filters={logFilters}
                  emptyState={
                    <TimelineEmptyState
                      title={t('History.emptyTitle', namespace)}
                      text={t('History.emptyText', namespace)}
                    />
                  }
                />
              </div>
              {shouldShowModal && <SubscriptionModal />}
            </div>
          </div>
        )}
      </Content>
    </>
  );
};
